import { useState } from 'react'
import { useEffect, useCallback, useRef } from 'react'
import { Image } from '../../types/image'
import { X, ChevronLeft, ChevronRight, ExternalLink, Info, RefreshCw, Play, Pause } from 'lucide-react'
import { useUpdateImage, useRefreshExif } from '../../hooks/useImages'
import { useAlbums } from '../../hooks/useAlbums'
import { adminApiClient, handleApiError } from '../../api/client'
import { useIdle } from '../../hooks/useIdle'
import { useAuthStore } from '../../stores/authStore'
import { getThumbnailUrl } from '../../lib/images'
import { DownloadOptions } from '../common/DownloadOptions'
import { formatDateTime } from '../../utils/formatting'

interface LightboxProps {
  images: Image[]
  currentIndex: number
  isOpen: boolean
  onClose: () => void
  onNavigate: (index: number) => void
  fetchNextPage?: () => Promise<unknown>
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
  fetchNextPageError?: Error | null
}

const PREFETCH_THRESHOLD = 5

const MAX_ZOOM = 4
const DOUBLE_CLICK_ZOOM = 2.5
const ZOOM_STEP = 1.25
const SLIDESHOW_INTERVAL_MS = 4000
const SWIPE_THRESHOLD_PX = 60

interface LightboxView {
  zoom: number
  pan: { x: number; y: number }
}

interface PointerGesture {
  pointers: Map<number, { x: number; y: number }>
  start: { x: number; y: number } | null
  startPan: { x: number; y: number }
  pinch: { dist: number; zoom: number; lastMid: { x: number; y: number } } | null
}

const clampZoom = (zoom: number) => Math.min(MAX_ZOOM, Math.max(1, zoom))

export const Lightbox: React.FC<LightboxProps> = ({
  images,
  currentIndex,
  isOpen,
  onClose,
  onNavigate,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPageError,
}) => {
  const currentImage = images[currentIndex]
  const [showInfo, setShowInfo] = useState(false)
  const [dateCaptured, setDateCaptured] = useState('')
  const [selectedAlbumId, setSelectedAlbumId] = useState(currentImage?.albumId || '')
  const [isEditing, setIsEditing] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [pageError, setPageError] = useState<string | null>(null)
  const [editError, setEditError] = useState<string | null>(null)
  const updateImage = useUpdateImage(adminApiClient)
  const refreshExif = useRefreshExif(adminApiClient)
  const { data: albums } = useAlbums(adminApiClient)
  const { isAuthenticated, user } = useAuthStore()
  const isAdmin = isAuthenticated && user?.isAdmin
  const isIdle = useIdle(3000)

  // Clear transient errors when navigating or closing.
  useEffect(() => {
    setPageError(null)
  }, [currentIndex, isOpen])

  // Surface errors from the parent query.
  useEffect(() => {
    if (fetchNextPageError) {
      setPageError(fetchNextPageError.message || 'Failed to load more photos')
    }
  }, [fetchNextPageError])

  // Prefetch the next page when the user nears the end of the loaded set.
  useEffect(() => {
    if (!isOpen || !hasNextPage || isFetchingNextPage || !fetchNextPage) return

    if (currentIndex >= images.length - PREFETCH_THRESHOLD) {
      fetchNextPage().catch((err: Error) => {
        setPageError(err?.message || 'Failed to load more photos')
      })
    }
  }, [
    isOpen,
    currentIndex,
    images.length,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  ])

  const handlePrevious = useCallback(() => {
    if (currentIndex > 0) {
      onNavigate(currentIndex - 1)
    }
  }, [currentIndex, onNavigate])

  const handleNext = useCallback(() => {
    if (currentIndex < images.length - 1) {
      onNavigate(currentIndex + 1)
    }
  }, [currentIndex, images.length, onNavigate])

  // ---- Zoom / pan / swipe gestures / slideshow ----
  const [view, setView] = useState<LightboxView>({ zoom: 1, pan: { x: 0, y: 0 } })
  const [isPlaying, setIsPlaying] = useState(false)
  const viewRef = useRef(view)
  const imageAreaRef = useRef<HTMLDivElement>(null)
  const mainImageRef = useRef<HTMLImageElement>(null)
  const gestureRef = useRef<PointerGesture>({
    pointers: new Map(),
    start: null,
    startPan: { x: 0, y: 0 },
    pinch: null,
  })

  const applyView = useCallback((next: LightboxView) => {
    viewRef.current = next
    setView(next)
  }, [])

  const resetView = useCallback(() => applyView({ zoom: 1, pan: { x: 0, y: 0 } }), [applyView])

  const toAreaPoint = useCallback((clientX: number, clientY: number) => {
    const rect = imageAreaRef.current?.getBoundingClientRect()
    return { x: clientX - (rect?.left ?? 0), y: clientY - (rect?.top ?? 0) }
  }, [])

  // Keep the scaled image inside the image area on both axes; center when it
  // overflows the area entirely.
  const clampPan = useCallback((zoomLevel: number, pan: { x: number; y: number }) => {
    const img = mainImageRef.current
    const area = imageAreaRef.current
    if (!img || !area || zoomLevel <= 1) return { x: 0, y: 0 }
    const w = img.offsetWidth
    const h = img.offsetHeight
    if (!w || !h) return { x: 0, y: 0 }
    const axis = (value: number, extent: number, slackStart: number, slackEnd: number) => {
      const min = ((zoomLevel - 1) * extent) / 2 - slackStart
      const max = slackEnd - ((zoomLevel - 1) * extent) / 2
      // When the scaled image overflows the area on this axis, both edges
      // cannot stay inside; allow panning between the opposite edges.
      if (min > max) return Math.min(Math.max(value, max), min)
      return Math.min(Math.max(value, min), max)
    }
    return {
      x: axis(pan.x, w, img.offsetLeft, area.clientWidth - img.offsetLeft - w),
      y: axis(pan.y, h, img.offsetTop, area.clientHeight - img.offsetTop - h),
    }
  }, [])

  // Zoom while keeping the anchored content point fixed in place:
  // t2 = X(1 - f) + f * t1 with f = nextZoom / currentZoom.
  const panForZoom = useCallback(
    (nextZoom: number, anchor?: { x: number; y: number }) => {
      const img = mainImageRef.current
      const current = viewRef.current
      if (!img || !anchor || !img.offsetWidth || !img.offsetHeight || current.zoom <= 0) {
        return { ...current.pan }
      }
      const f = nextZoom / current.zoom
      return clampPan(nextZoom, {
        x: (anchor.x - img.offsetLeft - img.offsetWidth / 2) * (1 - f) + f * current.pan.x,
        y: (anchor.y - img.offsetTop - img.offsetHeight / 2) * (1 - f) + f * current.pan.y,
      })
    },
    [clampPan]
  )

  // Pause playback whenever the zoom level actually changes.
  const zoomTo = useCallback(
    (nextZoom: number, anchor?: { x: number; y: number }) => {
      const clamped = Math.min(MAX_ZOOM, Math.max(1, nextZoom))
      if (clamped !== viewRef.current.zoom) setIsPlaying(false)
      applyView({ zoom: clamped, pan: panForZoom(clamped, anchor) })
    },
    [applyView, panForZoom]
  )

  const handleImagePointerDown = (e: React.PointerEvent<HTMLImageElement>) => {
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId)
    } catch {
      // jsdom and some drivers reject capture for inactive pointers; gestures
      // still work via direct events on the image element.
    }
    const g = gestureRef.current
    g.pointers.set(e.pointerId, toAreaPoint(e.clientX, e.clientY))
    if (g.pointers.size === 1) {
      g.start = toAreaPoint(e.clientX, e.clientY)
      g.startPan = { ...viewRef.current.pan }
    } else if (g.pointers.size === 2 && g.start) {
      const [a, b] = [...g.pointers.values()]
      g.pinch = {
        dist: Math.max(Math.hypot(a.x - b.x, a.y - b.y), 1),
        zoom: viewRef.current.zoom,
        lastMid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
      }
    }
  }

  const handleImagePointerMove = (e: React.PointerEvent<HTMLImageElement>) => {
    const g = gestureRef.current
    if (!g.pointers.has(e.pointerId)) return
    const point = toAreaPoint(e.clientX, e.clientY)
    g.pointers.set(e.pointerId, point)

    if (g.pointers.size === 2 && g.pinch) {
      const [a, b] = [...g.pointers.values()]
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
      const dist = Math.hypot(a.x - b.x, a.y - b.y)
      const nextZoom = clampZoom((g.pinch.zoom * dist) / g.pinch.dist)
      const img = mainImageRef.current
      const pan = { ...viewRef.current.pan }
      if (img && img.offsetWidth && img.offsetHeight) {
        // Anchor the content point currently under the fingers, then follow
        // the midpoint delta since the previous event (computed per event so
        // stationary fingers never accumulate translation).
        const f = nextZoom / viewRef.current.zoom
        pan.x =
          (mid.x - img.offsetLeft - img.offsetWidth / 2) * (1 - f) +
          f * pan.x +
          (mid.x - g.pinch.lastMid.x)
        pan.y =
          (mid.y - img.offsetTop - img.offsetHeight / 2) * (1 - f) +
          f * pan.y +
          (mid.y - g.pinch.lastMid.y)
      } else {
        pan.x += mid.x - g.pinch.lastMid.x
        pan.y += mid.y - g.pinch.lastMid.y
      }
      g.pinch.lastMid = mid
      if (nextZoom !== viewRef.current.zoom) setIsPlaying(false)
      applyView({ zoom: nextZoom, pan: clampPan(nextZoom, pan) })
      return
    }

    if (viewRef.current.zoom > 1 && g.start) {
      const pan = {
        x: g.startPan.x + (point.x - g.start.x),
        y: g.startPan.y + (point.y - g.start.y),
      }
      applyView({ zoom: viewRef.current.zoom, pan: clampPan(viewRef.current.zoom, pan) })
    }
  }

  const handleImagePointerUp = (e: React.PointerEvent<HTMLImageElement>) => {
    const g = gestureRef.current
    if (!g.pointers.has(e.pointerId)) return
    g.pointers.delete(e.pointerId)

    if (g.pointers.size === 1) {
      // A pinch ended with one finger left; keep panning with it.
      const [p] = [...g.pointers.values()]
      g.start = p
      g.startPan = { ...viewRef.current.pan }
      g.pinch = null
      return
    }
    if (g.pointers.size > 1) return

    const start = g.start
    g.pinch = null
    g.start = null
    if (!start || viewRef.current.zoom > 1) return

    const dx = toAreaPoint(e.clientX, e.clientY).x - start.x
    const dy = toAreaPoint(e.clientX, e.clientY).y - start.y
    const dominated = Math.abs(dx) > Math.abs(dy)
    if (dx <= -SWIPE_THRESHOLD_PX && dominated) {
      e.preventDefault()
      handleNext()
    } else if (dx >= SWIPE_THRESHOLD_PX && dominated) {
      e.preventDefault()
      handlePrevious()
    }
  }

  const handleImagePointerCancel = () => {
    const g = gestureRef.current
    g.pointers.clear()
    g.start = null
    g.pinch = null
  }

  const handleImageDoubleClick = (e: React.MouseEvent<HTMLImageElement>) => {
    e.stopPropagation()
    if (viewRef.current.zoom > 1) {
      resetView()
    } else {
      zoomTo(DOUBLE_CLICK_ZOOM, toAreaPoint(e.clientX, e.clientY))
    }
  }

  // Reset zoom/pan when navigating to a different image.
  useEffect(() => {
    resetView()
  }, [currentIndex, resetView])

  // Reset zoom and stop playback when the lightbox closes.
  useEffect(() => {
    if (!isOpen && (viewRef.current.zoom !== 1 || isPlaying)) {
      resetView()
      setIsPlaying(false)
    }
  }, [isOpen, isPlaying, resetView])

  // Zoom with the wheel (non-passive so preventDefault works over the image).
  useEffect(() => {
    const area = imageAreaRef.current
    if (!area || !isOpen) return
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      const nextZoom = viewRef.current.zoom * (e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP)
      if (nextZoom === viewRef.current.zoom) return
      zoomTo(nextZoom, toAreaPoint(e.clientX, e.clientY))
    }
    area.addEventListener('wheel', onWheel, { passive: false })
    return () => area.removeEventListener('wheel', onWheel)
  }, [isOpen, zoomTo, toAreaPoint])

  // Auto-advance while the slideshow plays; restart the timer on navigation.
  useEffect(() => {
    if (!isOpen || !isPlaying) return
    const id = window.setInterval(() => {
      if (currentIndex < images.length - 1) {
        onNavigate(currentIndex + 1)
      } else {
        onNavigate(0)
      }
    }, SLIDESHOW_INTERVAL_MS)
    return () => window.clearInterval(id)
  }, [isOpen, isPlaying, currentIndex, images.length, onNavigate])

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose()
          break
        case 'ArrowLeft':
          handlePrevious()
          break
        case 'ArrowRight':
          handleNext()
          break
        case ' ': {
          // Space toggles the slideshow unless a control has focus.
          const target = e.target as HTMLElement | null
          if (!target?.closest('button, input, select, textarea, a')) {
            e.preventDefault()
            setIsPlaying((playing) => !playing)
          }
          break
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose, handlePrevious, handleNext])

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  // Sync state on image navigation
  useEffect(() => {
    if (currentImage) {
      setDateCaptured(currentImage.dateCaptured || '')
      setSelectedAlbumId(currentImage.albumId)
      setIsEditing(false)
      setEditError(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentImage?.imageId])

  if (!isOpen || !currentImage) return null

  const imageUrl = getThumbnailUrl(currentImage, 'large')
  const hasPrevious = currentIndex > 0
  const hasNext = currentIndex < images.length - 1

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex flex-col" role="dialog" aria-modal="true" aria-label="Image viewer">
      {/* Header (in-flow so the image below can never slide underneath it) */}
      <div className={`relative z-10 flex items-center justify-between px-4 py-3 bg-black/80 backdrop-blur-sm transition-opacity duration-300 ${isIdle ? 'opacity-0' : 'opacity-100'}`}>
        <div className="text-white">
          <h3 className="font-medium">{currentImage.filename}</h3>
          <p className="text-sm text-white/60">
            {currentIndex + 1} of {images.length} • {currentImage.width}×{currentImage.height}
          </p>
          {pageError && (
            <p className="text-sm text-red-400 mt-1" role="alert">
              {pageError}
            </p>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <DownloadOptions image={currentImage} defaultSize="large" variant="icon" />
          <a
            href={`/image/${currentImage.imageId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            aria-label="View image details"
            title="View Details"
          >
            <ExternalLink className="w-5 h-5" />
          </a>
          <button
            onClick={() => setShowInfo(!showInfo)}
            className={`p-2 rounded-lg transition-colors ${
              showInfo ? 'text-white bg-white/20' : 'text-white/70 hover:text-white hover:bg-white/10'
            }`}
            aria-label={showInfo ? "Hide image info" : "Show image info"}
            title="Image Info"
          >
            <Info className="w-5 h-5" />
          </button>
          <button
            onClick={() => setIsPlaying((playing) => !playing)}
            className={`p-2 rounded-lg transition-colors ${
              isPlaying ? 'text-white bg-white/20' : 'text-white/70 hover:text-white hover:bg-white/10'
            }`}
            aria-label={isPlaying ? 'Pause slideshow' : 'Start slideshow'}
            title={isPlaying ? 'Pause slideshow (Space)' : 'Slideshow (Space)'}
          >
            {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
          </button>
          <button
            onClick={onClose}
            className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            aria-label="Close image viewer"
            title="Close (Esc)"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>

      {showInfo && (
        <div className="absolute top-16 right-4 w-72 bg-black/80 backdrop-blur-sm rounded-lg p-4 text-white z-10">
          <h4 className="font-medium mb-3">Image Details</h4>
          <div className="space-y-3 text-sm">
            <div>
              <p className="text-white/60 text-xs uppercase mb-1">Filename</p>
              <p className="truncate">{currentImage.filename}</p>
            </div>
            <div>
              <p className="text-white/60 text-xs uppercase mb-1">Dimensions</p>
              <p>{currentImage.width} × {currentImage.height}</p>
            </div>
            <div>
              <p className="text-white/60 text-xs uppercase mb-1">File Size</p>
              <p>{(currentImage.fileSize / 1024 / 1024).toFixed(2)} MB</p>
            </div>
            <div>
              <p className="text-white/60 text-xs uppercase mb-1">Album</p>
              {isEditing ? (
                <div className="space-y-2">
                  <select
                    value={selectedAlbumId}
                    onChange={(e) => setSelectedAlbumId(e.target.value)}
                    className="w-full px-2 py-1 text-black rounded text-xs"
                    disabled={updateImage.isPending}
                  >
                    {albums?.map((album) => (
                      <option key={album.albumId} value={album.albumId}>
                        {album.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <p className="truncate">
                  {albums?.find((album) => album.albumId === currentImage.albumId)?.name || currentImage.albumId}
                </p>
              )}
            </div>
            <div>
              <p className="text-white/60 text-xs uppercase mb-1">Date Captured</p>
              {isEditing ? (
                <div className="space-y-2">
                  <input
                    type="datetime-local"
                    value={dateCaptured}
                    onChange={(e) => setDateCaptured(e.target.value)}
                    className="w-full px-2 py-1 text-black rounded text-xs"
                    disabled={updateImage.isPending}
                  />
                  {editError && (
                    <p className="text-red-400 text-xs" role="alert">
                      {editError}
                    </p>
                  )}
                  <div className="flex gap-2">
                    <button
                      onClick={async () => {
                        setEditError(null)
                        try {
                          const updates: Partial<Image> = { dateCaptured }
                          if (selectedAlbumId !== currentImage.albumId) {
                            updates.albumId = selectedAlbumId
                          }
                          await updateImage.mutateAsync({
                            id: currentImage.imageId,
                            data: updates
                          })
                          setIsEditing(false)
                        } catch (error) {
                          setEditError(handleApiError(error as Error))
                        }
                      }}
                      className="flex-1 px-2 py-1 bg-blue-600 hover:bg-blue-700 rounded text-xs"
                      disabled={updateImage.isPending || !selectedAlbumId}
                    >
                      {updateImage.isPending ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => {
                        setDateCaptured(currentImage.dateCaptured || '')
                        setSelectedAlbumId(currentImage.albumId)
                        setEditError(null)
                        setIsEditing(false)
                      }}
                      className="px-2 py-1 bg-gray-600 hover:bg-gray-700 rounded text-xs"
                      disabled={updateImage.isPending}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <p>{dateCaptured ? formatDateTime(dateCaptured) : 'Not set'}</p>
                  {isAdmin && (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="text-blue-400 hover:text-blue-300 text-xs"
                    >
                      Edit
                    </button>
                  )}
                </div>
              )}
            </div>
            {currentImage.exifData && Object.keys(currentImage.exifData).length > 0 && (
              <div className="pt-3 border-t border-white/10">
                <p className="text-white/60 text-xs uppercase mb-2">Camera Info</p>
                {currentImage.exifData.camera && (
                  <p className="text-white/90">{currentImage.exifData.camera}</p>
                )}
                {currentImage.exifData.lens && (
                  <p className="text-white/60 text-xs">{currentImage.exifData.lens}</p>
                )}
                <div className="flex gap-3 mt-1 text-xs text-white/60">
                  {currentImage.exifData.aperture && <span>{currentImage.exifData.aperture}</span>}
                  {currentImage.exifData.shutterSpeed && <span>{currentImage.exifData.shutterSpeed}</span>}
                  {currentImage.exifData.iso && <span>ISO {currentImage.exifData.iso}</span>}
                  {currentImage.exifData.focalLength && <span>{currentImage.exifData.focalLength}</span>}
                </div>
              </div>
            )}
            {isAdmin && (
              <div className="pt-3 border-t border-white/10">
                <button
                  onClick={async () => {
                    setIsRefreshing(true)
                    try {
                      await refreshExif.mutateAsync(currentImage.imageId)
                    } catch (error) {
                      console.error('Failed to refresh EXIF:', error)
                    }
                    setIsRefreshing(false)
                  }}
                  disabled={isRefreshing}
                  className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded-lg text-xs text-white transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`w-3 h-3 ${isRefreshing ? 'animate-spin' : ''}`} />
                  {isRefreshing ? 'Refreshing...' : 'Refresh EXIF'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Image Area */}
      <div ref={imageAreaRef} data-testid="lightbox-image-area" className="flex-1 min-h-0 relative flex items-center justify-center overflow-hidden p-4">
        {/* Previous Button */}
        {hasPrevious && (
          <button
            onClick={handlePrevious}
            className="absolute left-4 z-10 p-3 text-white/70 hover:text-white bg-black/30 hover:bg-black/50 rounded-full transition-all"
            aria-label="Previous image"
            title="Previous (←)"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>
        )}

        {/* Image */}
        <img
          ref={mainImageRef}
          src={imageUrl}
          alt={currentImage.filename}
          draggable={false}
          className={`max-w-full max-h-full object-contain select-none ${
            view.zoom > 1 ? 'cursor-grab' : 'cursor-zoom-in'
          }`}
          style={{
            touchAction: 'none',
            transform: `translate(${view.pan.x}px, ${view.pan.y}px) scale(${view.zoom})`,
          }}
          onDoubleClick={handleImageDoubleClick}
          onPointerDown={handleImagePointerDown}
          onPointerMove={handleImagePointerMove}
          onPointerUp={handleImagePointerUp}
          onPointerCancel={handleImagePointerCancel}
          onClick={(e) => e.stopPropagation()}
        />

        {/* Next Button */}
        {hasNext && (
          <button
            onClick={handleNext}
            disabled={isFetchingNextPage}
            className="absolute right-4 z-10 p-3 text-white/70 hover:text-white bg-black/30 hover:bg-black/50 rounded-full transition-all disabled:opacity-50"
            aria-label="Next image"
            title="Next (→)"
          >
            {isFetchingNextPage ? (
              <RefreshCw className="w-8 h-8 animate-spin" />
            ) : (
              <ChevronRight className="w-8 h-8" />
            )}
          </button>
        )}

        {/* Click outside to close */}
        <div 
          className="absolute inset-0 -z-10"
          onClick={onClose}
        />
      </div>

      {/* Keyboard Shortcuts Hint */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/40 text-xs flex items-center gap-4">
        <span>← Previous</span>
        <span>→ Next</span>
        <span>Space Slideshow</span>
        <span>Esc Close</span>
      </div>
    </div>
  )
}